import { useEffect, useId, useState, type ReactNode } from 'react'
import { AlertTriangle, Check, Lock } from 'lucide-react'
import type { Berekend, Keuze } from '../../../gedeeld/types'

/* ===========================================================================
 *  Bouwstenen van de Exact-kaarten (ExactFormulier en de kaart per functie)
 *
 *  Klein gehouden en zonder eigen wil: een veld laat zien wat er staat en
 *  geeft door wat je typt. Nakijken doet de server (exact-controleren); wat
 *  hier gebeurt is alleen dat een half getypt bedrag ("12,") niet stilletjes
 *  0 wordt, en dat een code die niet in de keuzelijst staat niet uit het
 *  veld verdwijnt. Een keuzelijst die een onbekende waarde wegpoetst, is een
 *  kaart die iets anders uitvoert dan de AI voorstelde zonder dat je het ziet.
 * =========================================================================== */

/**
 * Vaste sleutels voor de regels van een boeking of factuur. Op volgorde
 * (index) ging het mis bij weghalen: het veld van regel 3 bleef dan staan
 * met de half getypte tekst van regel 2.
 */
let regelTeller = 0
export function useSleutels(aantal: number) {
  const [ids, setIds] = useState(() => Array.from({ length: aantal }, () => ++regelTeller))
  let lijst = ids
  if (ids.length !== aantal) {
    /* Van buiten veranderd (de server gaf een ander aantal regels terug). */
    lijst = ids.length > aantal ? ids.slice(0, aantal) : [...ids, ...Array.from({ length: aantal - ids.length }, () => ++regelTeller)]
    setIds(lijst)
  }
  return {
    ids: lijst,
    erbij: () => setIds([...lijst, ++regelTeller]),
    weg: (i: number) => setIds(lijst.filter((_, j) => j !== i)),
  }
}

/** "1.234,56" of "1234.56" of "-12,5" -> getal; '' -> null; onzin -> NaN. */
export function leesGetal(tekst: string, decimalen = 2): number | null {
  let t = tekst.trim().replace(/[\s€]/g, '').replace(/−/g, '-')
  if (!t) return null
  /* Met een komma is de komma de decimaal en zijn punten duizendtallen. */
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.')
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(t)) return Number.NaN
  const f = 10 ** decimalen
  return Math.round(Number(t) * f) / f
}

export function toonGetal(n: number | null | undefined, decimalen = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return ''
  return (decimalen === 2 ? n.toFixed(2) : String(n)).replace('.', ',')
}

export function euro(n: number | null | undefined, valuta?: string): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '–'
  try {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: (valuta || 'EUR').toUpperCase() }).format(n)
  } catch {
    return `${valuta || 'EUR'} ${toonGetal(n)}`
  }
}

/** Waar een naam in berekend.namen bij hoort; de sleutel is "soort:code". */
export type NaamSoort = 'rekening' | 'dagboek' | 'btw' | 'relatie' | 'kostenplaats' | 'kostendrager' | 'artikel'

/**
 * De naam die de server bij een code vond. Met de soort ervoor, want codes
 * botsen: dagboek "70" en artikel "70" zijn twee verschillende dingen.
 */
export function naamVan(berekend: Berekend | undefined, soort: NaamSoort, code: string | undefined): string | null {
  const c = (code ?? '').trim()
  if (!c) return null
  return berekend?.namen?.[`${soort}:${c}`] ?? null
}

/**
 * Een getal (bedrag, aantal). Houdt de tekst zelf bij, zodat "12," of "-"
 * tijdens het typen blijft staan; geeft alleen een getal door als het er
 * een is. Een ongeldig getal meldt hij met `onOngeldig`, zodat de kaart
 * Uitvoeren kan tegenhouden: anders ging de vorige waarde mee terwijl er
 * iets anders op het scherm staat.
 */
export function GetalVeld({ label, waarde, onWaarde, onOngeldig, decimalen = 2, leegMag = false, placeholder, breed, verbergLabel }: {
  label: string
  waarde: number | null | undefined
  onWaarde: (n: number | null) => void
  onOngeldig: (ja: boolean) => void
  decimalen?: number
  leegMag?: boolean
  placeholder?: string
  breed?: boolean
  verbergLabel?: boolean
}) {
  const id = useId()
  const [tekst, setTekst] = useState(() => toonGetal(waarde, decimalen))
  const gelezen = leesGetal(tekst, decimalen)
  const fout = gelezen === null ? (leegMag ? null : 'Vul een getal in.') : Number.isNaN(gelezen) ? 'Dit is geen getal.' : null

  /* Van buiten veranderd (de server rondde af, een regel schoof op): de
     tekst volgen. Niet als de tekst al op die waarde uitkomt -- dan zou
     "12," tijdens het typen terugspringen naar "12,00". */
  useEffect(() => {
    const nu = leesGetal(tekst, decimalen)
    if (nu !== (waarde ?? null)) setTekst(toonGetal(waarde, decimalen))
  }, [waarde]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { onOngeldig(!!fout) }, [!!fout]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onOngeldig(false), []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <label className={`veld ${breed ? 'breed' : ''}`}>
      <span className={verbergLabel ? 'verborgen' : 'veld-label'}>{label}</span>
      <input className="invoer mono ex-getal" inputMode="decimal" value={tekst} placeholder={placeholder}
        aria-invalid={!!fout} aria-describedby={fout ? `${id}-f` : undefined}
        onChange={(e) => {
          setTekst(e.target.value)
          const n = leesGetal(e.target.value, decimalen)
          if (n === null ? leegMag : !Number.isNaN(n)) onWaarde(n)
        }} />
      {fout && <span className="veld-fout" id={`${id}-f`}>{fout}</span>}
    </label>
  )
}

/**
 * Een code uit een keuzelijst (dagboek, btw, kostenplaats, artikel). Zonder
 * lijst een gewoon tekstveld. Staat de waarde niet in de lijst, dan blijft
 * hij kiesbaar als "(niet gevonden)" in plaats van stil te verdwijnen.
 */
export function KeuzeVeld({ label, waarde, onWaarde, keuzes, leeg, breed, onder, verbergLabel, placeholder }: {
  label: string
  waarde: string | undefined
  onWaarde: (w: string) => void
  keuzes: Keuze[] | undefined
  /** De tekst voor "niets gekozen"; zonder: geen lege keuze. */
  leeg?: string
  breed?: boolean
  onder?: ReactNode
  verbergLabel?: boolean
  placeholder?: string
}) {
  const w = waarde ?? ''
  const lijst = keuzes ?? []
  const onbekend = w && !lijst.some((k) => k.code === w)
  return (
    <label className={`veld ${breed ? 'breed' : ''}`}>
      <span className={verbergLabel ? 'verborgen' : 'veld-label'}>{label}</span>
      {lijst.length ? (
        <select className="invoer" value={w} onChange={(e) => onWaarde(e.target.value)} aria-invalid={onbekend ? true : undefined}>
          {(leeg !== undefined || !w) && <option value="">{leeg ?? 'Kies…'}</option>}
          {onbekend && <option value={w}>{w} (niet gevonden)</option>}
          {lijst.map((k) => <option key={k.code} value={k.code}>{k.code} · {k.naam}{k.extra ? ` (${k.extra})` : ''}</option>)}
        </select>
      ) : (
        <input className="invoer mono" value={w} placeholder={placeholder ?? (leeg ? `Leeg = ${leeg.toLowerCase()}` : undefined)}
          onChange={(e) => onWaarde(e.target.value)} />
      )}
      {onder}
    </label>
  )
}

/** Een tekstveld met eventueel de naam die de server erbij vond eronder. */
export function TekstVeld({ label, waarde, onWaarde, mono, breed, placeholder, naam, nietGevonden, verbergLabel, maxLength }: {
  label: string
  waarde: string | undefined
  onWaarde: (w: string) => void
  mono?: boolean
  breed?: boolean
  placeholder?: string
  /** De naam bij deze code, als de server die vond. */
  naam?: string | null
  /** Laat "niet gevonden" zien als er geen naam is (alleen zinvol na een controle). */
  nietGevonden?: boolean
  verbergLabel?: boolean
  maxLength?: number
}) {
  const w = waarde ?? ''
  return (
    <label className={`veld ${breed ? 'breed' : ''}`}>
      <span className={verbergLabel ? 'verborgen' : 'veld-label'}>{label}</span>
      <input className={`invoer ${mono ? 'mono' : ''}`} value={w} placeholder={placeholder} maxLength={maxLength}
        onChange={(e) => onWaarde(e.target.value)} />
      {naam ? <span className="ex-naam">{naam}</span> : nietGevonden && w.trim() ? <span className="ex-naam onbekend">niet gevonden</span> : null}
    </label>
  )
}

export function DatumVeld({ label, waarde, onWaarde, breed, verbergLabel }: {
  label: string
  waarde: string | undefined
  onWaarde: (w: string) => void
  breed?: boolean
  verbergLabel?: boolean
}) {
  return (
    <label className={`veld ${breed ? 'breed' : ''}`}>
      <span className={verbergLabel ? 'verborgen' : 'veld-label'}>{label}</span>
      <input type="date" className="invoer" value={waarde ?? ''} onChange={(e) => onWaarde(e.target.value)} />
    </label>
  )
}

/** Keuzerondjes als knoppen, met de uitleg van de gekozen keuze eronder. */
export function Rondjes<T extends string>({ label, waarde, onWaarde, keuzes, breed }: {
  label: string
  waarde: T | ''
  onWaarde: (w: T) => void
  keuzes: { key: T; label: string; kort?: string; uitleg?: string; gevaar?: boolean }[]
  breed?: boolean
}) {
  const id = useId()
  const gekozen = keuzes.find((k) => k.key === waarde)
  return (
    <div className={`veld ${breed ? 'breed' : ''}`}>
      <span className="veld-label" id={`${id}-l`}>{label}</span>
      <div className="segment" role="radiogroup" aria-labelledby={`${id}-l`} aria-describedby={`${id}-u`}>
        {keuzes.map((k) => (
          <button key={k.key} type="button" role="radio" aria-checked={waarde === k.key}
            className={`segment-knop ${k.gevaar ? 't-danger' : ''} ${waarde === k.key ? 'aan' : ''}`}
            onClick={() => onWaarde(k.key)}>
            {k.kort && <strong className="mono">{k.kort}</strong>}{k.label}
          </button>
        ))}
      </div>
      <span className={`ex-naam ${gekozen ? '' : 'onbekend'}`} id={`${id}-u`}>{gekozen ? gekozen.uitleg ?? '' : 'Nog niet gekozen'}</span>
    </div>
  )
}

/**
 * De bv's om in aan te maken (grootboek, kostenplaats), met per bv wat de
 * server zag: bestaat de code daar al, en onder welke naam.
 */
export function BvVinkjes({ mogelijk, gekozen, onGekozen, perBv, actie, code }: {
  mogelijk: { code: string; naam: string }[]
  gekozen: string[]
  onGekozen: (d: string[]) => void
  perBv: Berekend['perBv']
  actie: 'aanmaken' | 'wijzigen' | 'blokkeren' | 'deblokkeren'
  code: string
}) {
  const alle = mogelijk.length > 1 && mogelijk.every((d) => gekozen.includes(d.code))
  return (
    <div className="veld breed">
      <span className="veld-label">In welke bv's ({gekozen.length} van {mogelijk.length})</span>
      <div className="relatie-bvs">
        {mogelijk.length > 2 && (
          <div className="relatie-bv">
            <label className="vinkje">
              <input type="checkbox" checked={alle} onChange={(e) => onGekozen(e.target.checked ? mogelijk.map((d) => d.code) : [])} />
              <span className="stil">Alle bv's</span>
            </label>
          </div>
        )}
        {mogelijk.map((d) => {
          const p = perBv?.find((x) => x.division === d.code)
          /* Bij aanmaken is "bestaat al" het probleem; bij wijzigen en
             (de)blokkeren juist "bestaat niet". */
          const probleem = p ? (actie === 'aanmaken' ? p.bestaat : !p.bestaat) : false
          return (
            <div key={d.code} className="relatie-bv">
              <label className="vinkje">
                <input type="checkbox" checked={gekozen.includes(d.code)}
                  onChange={(e) => onGekozen(e.target.checked ? [...gekozen, d.code] : gekozen.filter((x) => x !== d.code))} />
                <span>{d.naam}</span>
              </label>
              {p && (
                <div className={`relatie-stand ${probleem ? 'warn' : 'ok'}`}>
                  {/* Eén span: .relatie-stand.warn is een kolom, en anders kwam
                      elk stukje op een eigen regel. */}
                  <span>
                    {probleem ? <AlertTriangle size={12} /> : <Check size={12} />}{' '}
                    {p.bestaat
                      ? <>Bestaat al: <span className="mono">{code}</span> {p.omschrijving ?? ''}{p.geblokkeerd ? <> · <Lock size={11} /> geblokkeerd</> : null}</>
                      : 'Bestaat hier nog niet'}
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Totalen zoals de server ze uitrekende. */
const TOTAAL_LABEL: Record<string, string> = {
  excl: 'Excl. btw', btw: 'Btw', incl: 'Incl. btw',
  debet: 'Debet', credit: 'Credit', verschil: 'Verschil',
  ontvangen: 'Ontvangen', betaald: 'Betaald', saldo: 'Saldo',
}

export function Totalen({ totalen, valuta, oud, uitleg }: {
  totalen: Record<string, number> | undefined
  valuta?: string
  oud: boolean
  uitleg?: ReactNode
}) {
  const rijen = Object.entries(totalen ?? {})
  return (
    <div className={`ex-totalen breed ${oud ? 'oud' : ''}`} aria-live="polite">
      {uitleg && <p className="ex-totalen-uitleg">{uitleg}</p>}
      {rijen.length ? (
        <dl>
          {rijen.map(([k, n]) => (
            <div key={k} className={`ex-totaal ${k === 'incl' || k === 'saldo' ? 'ex-hoofd' : ''} ${k === 'verschil' && Math.abs(n) >= 0.005 ? 'fout' : ''}`}>
              <dt>{TOTAAL_LABEL[k] ?? k}</dt>
              <dd className="mono">{euro(n, valuta)}</dd>
            </div>
          ))}
        </dl>
      ) : <p className="stil">Nog geen totalen.</p>}
      {oud && <span className="ex-totalen-oud">wordt opnieuw uitgerekend…</span>}
    </div>
  )
}
